import { ProductV2DTO } from '@/types/application/inventory-management/ProductV2DTO';
import { ProductVariantDTO } from '@/types/application/inventory-management/ProductVariantDTO';
import { SupplierDTO } from '../accounts-payable/useSuppliers';
import { InboundEmailDTO } from '@/types/application/InboundEmailDTO';

export interface SupplierProductDTO {
  id: string;
  organizationId: string;
  supplierId: string;
  productId: string;
  productVariantId: string;
  name: string;
  description?: string | null;
  sku?: string | null;
  unitPrice: number;

  // Supplier identifier fields
  supplierDescription?: string | null;
  supplierMainIdentifier?: string | null;
  supplierSku?: string | null;
  supplierUpc?: string | null;
  supplierPlu?: string | null;
  supplierEan?: string | null;
  supplierGtin?: string | null;
  supplierAsin?: string | null;
  supplierMpn?: string | null;
  supplierIsbn?: string | null;
  supplierNsn?: string | null;
  supplierFreeform?: string[] | null;

  createdAt: string;
  updatedAt: string;

  // Related entities (when included)
  supplier?: SupplierDTO;
  product?: ProductV2DTO;
  productVariant?: ProductVariantDTO;
}

export enum QuoteSheetLineItemAvailability {
  AVAILABLE = 'AVAILABLE',
  UNKNOWN = 'UNKNOWN',
  OUT_OF_STOCK = 'OUT_OF_STOCK',
}

export interface QuoteSheetLineItemDTO {
  id: string;
  organizationId: string;
  quoteSheetId: string;
  suggestedProductId?: string | null;
  suggestedProductVariantId?: string | null;
  suggestedSupplierProductId?: string | null;
  selectedProductId?: string | null;
  selectedSupplierProductId?: string | null;
  availability?: QuoteSheetLineItemAvailability | null;
  unitPrice?: string | number | null;
  locationRaw?: string | null;
  annotations?: string[] | null;
  rawData?: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;

  // Related entities (populated when included)
  suggestedSupplierProduct?: SupplierProductDTO;
  selectedSupplierProduct?: SupplierProductDTO;
  suggestedProduct?: ProductV2DTO;
  suggestedProductVariant?: ProductVariantDTO;
  selectedProduct?: ProductV2DTO;
  quoteSheet?: QuoteSheetDTO;
}

// Request type for upserting line items
export type UpsertQuoteSheetLineItemRequest = Pick<
  QuoteSheetLineItemDTO,
  | 'id'
  | 'suggestedProductId'
  | 'suggestedProductVariantId'
  | 'selectedProductId'
  | 'selectedSupplierProductId'
  | 'availability'
  | 'unitPrice'
> & {
  id?: string; // Optional for creating new items
  selectedProductVariantId?: string;
};

export interface UpsertQuoteSheetLineItemsRequest {
  lineItems: UpsertQuoteSheetLineItemRequest[];
}

// Response type for upsert operation
export interface UpsertQuoteSheetLineItemsResponse {
  data: QuoteSheetLineItemDTO[];
}

export interface QuoteSheetDTO {
  id: string;
  organizationId: string;
  supplierId: string;
  quoteDate?: string | null; // ISO date string
  currency?: string | null;
  notes?: string | null; // From update schema, may not be implemented yet
  rawData?: Record<string, unknown> | null;
  processingErrors?: Record<string, unknown> | null;
  logs?: Record<string, unknown>[] | null;
  createdAt: string;
  updatedAt: string;

  // Related entities (populated when included)
  supplier?: SupplierDTO;
  lineItems?: QuoteSheetLineItemDTO[];

  inboundEmail?: InboundEmailDTO;
}

// Request type for updating quote sheet
export interface UpdateQuoteSheetRequest {
  quoteDate?: string; // ISO datetime string
  currency?: string;
  notes?: string;
  supplierId?: string;
}

// Response type for update operation
export interface UpdateQuoteSheetResponse {
  success: true;
  data: QuoteSheetDTO;
}

// Response type for get single quote sheet
export interface GetQuoteSheetResponse {
  success: true;
  data: QuoteSheetDTO;
}

export interface QuoteSheetFilters {
  supplierId?: string;
  productId?: string;
  productVariantId?: string;
  dateFrom?: string; // ISO date string
  dateTo?: string; // ISO date string
  search?: string;
  limit?: number; // Default: 50, Max: 100
  offset?: number; // Default: 0
}

// Dashboard data types
export interface QuoteSheetDashboardFilters {
  supplierId?: string;
  productId?: string;
  productVariantId?: string;
  dateFrom?: Date;
  dateTo?: Date;
  search?: string;
  productMatch?: 'matched' | 'partial' | 'unmatched' | 'all';
}

export interface QuoteSheetDashboardResponse {
  quoteSheetLineItemsByDate: QuoteSheetLineItemDTO[];
}

export interface PaginationInfo {
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

export interface ListQuoteSheetsResponse {
  success: true;
  quoteSheets: QuoteSheetDTO[];
  pagination: PaginationInfo;
}
